import { invokeService, useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { partiallyMaskValue } from '@dxb-web-ui-framework/common/utils/DataObfuscation';
import { FormBuilderPresenter } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/FormBuilderPresenter';
import { Box, makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import React, { useState, useCallback, useEffect, VFC } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserInfo } from '../../../../../../../../userProfile/src/contexts/UserInfo/hooks';
import { IAuthorizationsService } from '../../../../../../../../iam/src/domain/Authorizations/entities/IAuthorizationsService';
import { useWizardErrorState as useClientSideWizardErrorState } from '../../../../../../contexts/ClientSideWizard/hooks';
import { useWizardErrorState as useProcessEngineWizardErrorState } from '../../../../../../contexts/ProcessEngineWizard/hooks';
import { ErrorAlert } from '../../../../../Generic/ErrorAlert/ErrorAlert';
import { inApp2FAConfig } from '../inApp2FAconfig';

export interface OTPProps {
  authorizationId: number;
  authorizationsService: IAuthorizationsService;
  onCancel: (data?: any) => void;
  onError: (data?: any) => void;
  onSuccess: (data?: any) => void;
  previousStepsValues: GenericObject;
  title?: string;
  caption?: string;
  subtitle?: string;
  mobile?: string;
  cancelable?: boolean;
  onToggleDrawer?: Function;
  shouldEnableOtpTitle?: boolean;
  shouldForceMobileOtpView?: boolean;
}

const useStyles = makeStyles(theme => ({
  title: {
    whiteSpace: 'pre-line',
  },
}));

//TODO: find a way to replace symbols in phone for UI
const OTP: VFC<OTPProps> = ({
  authorizationId,
  authorizationsService,
  onCancel,
  onError,
  onSuccess,
  previousStepsValues,
  title,
  mobile,
  caption,
  subtitle,
  cancelable,
  onToggleDrawer,
  shouldEnableOtpTitle,
  shouldForceMobileOtpView,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const clientSideWizardErrorState = useClientSideWizardErrorState();
  const processEngineWizardErrorState = useProcessEngineWizardErrorState();
  const classes = useStyles();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  // State
  const [error, setError] = useState<GenericObject<any>>();

  useEffect(() => {
    if (clientSideWizardErrorState.errors) {
      setError(clientSideWizardErrorState.errors);
    }
    if (processEngineWizardErrorState) {
      setError(processEngineWizardErrorState);
    }
  }, [clientSideWizardErrorState, processEngineWizardErrorState]);

  const onConfirmOTP = useCallback(
    async (otpCode: any) => await invokeService(() => authorizationsService?.ConfirmOTP(otpCode, onSuccess, onError)),
    [authorizationsService, onSuccess, onError],
  );

  //  TODO: use messageKey instead of code to find the error.
  useEffect(() => {
    const closeDrawer = error?.headers?.find(({ code }: { code: string }) => code === '404');

    if (closeDrawer) {
      setTimeout(() => {
        onToggleDrawer && onToggleDrawer();
      }, 3000);
    }
  }, [error, onToggleDrawer]);

  return (
    <Box height='100%' data-testid={'inApp2FAStep-OTP'}>
      {title && shouldEnableOtpTitle && (
        <Box mb={isMobile ? 5 : 3}>
          <Typography className={classes.title} color='secondary' variant={isMobile ? 'h6' : 'h4'}>
            {title}
          </Typography>
        </Box>
      )}
      <Box mb={1}>
        {error && !error.headers && <ErrorAlert message={`${error.message}`} />}
        {error &&
          error.headers &&
          error.headers.map(({ message, code }: { message: string; code: string }) => {
            if (code === '404') {
              return (
                <ErrorAlert
                  message={`${message} ${t('security.settings.changePasswordWizard.OTP.expired.ErrorMessage')}`}
                  key={message + ' ' + code}
                />
              );
            }
            return <ErrorAlert message={message} key={message + ' ' + code} />;
          })}
      </Box>
      <Box>
        <Typography
          variant={isMobile || shouldForceMobileOtpView ? 'subtitle1' : 'body2'}
          color={isMobile || shouldForceMobileOtpView ? 'primary' : 'textSecondary'}>
          {shouldForceMobileOtpView
            ? subtitle
              ? subtitle
              : t('content-composer.components.InApp2FA.InApp2FAMethods.OTP.Text1-Mobile')
            : t(
                isMobile
                  ? 'content-composer.components.InApp2FA.InApp2FAMethods.OTP.Text1-Mobile'
                  : 'content-composer.components.InApp2FA.InApp2FAMethods.OTP.Text1',
              )}
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          {shouldForceMobileOtpView
            ? t('content-composer.components.InApp2FA.InApp2FAMethods.OTP.Text2-Mobile')
            : t(
                isMobile
                  ? 'content-composer.components.InApp2FA.InApp2FAMethods.OTP.Text2-Mobile'
                  : 'content-composer.components.InApp2FA.InApp2FAMethods.OTP.Text2',
              )}
          {caption ? caption : `${partiallyMaskValue(mobile ?? userInfo?.phoneNumber, 4)}`}
        </Typography>
      </Box>
      <FormBuilderPresenter
        submitAction={onConfirmOTP}
        formConfig={inApp2FAConfig({ authorizationId })}
        externalEffectCallbacks={{
          authorizationsService,
          authorizationId,
          onExit: onCancel,
        }}
        externalProps={{
          cancelable,
        }}
      />
    </Box>
  );
};

export { OTP };
